import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, DOC_CONFIG } from "@/config/site";

function scrollTo(href) {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const handleNav = (href) => {
    setOpen(false);
    setTimeout(() => scrollTo(href), 10);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "border-b border-white/10 bg-[#030305]/80 backdrop-blur-xl" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-5 sm:h-20 sm:px-8">
          <button
            onClick={() => handleNav("#hero")}
            className="flex items-center gap-2.5"
            data-testid="nav-logo"
          >
            <span className="relative flex h-7 w-7 items-center justify-center rounded-md border border-white/15 bg-gradient-to-br from-indigo-500/30 to-violet-500/20">
              <span className="h-2 w-2 rounded-sm bg-indigo-400" />
            </span>
            <span className="font-display text-sm font-bold uppercase tracking-[0.22em] text-white">
              Xenovia&nbsp;Prime
            </span>
          </button>

          <nav className="hidden items-center gap-9 lg:flex">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => handleNav(l.href)}
                className="group relative font-mono text-[12px] uppercase tracking-[0.16em] text-gray-400 transition-colors hover:text-white"
                data-testid={`nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-indigo-400 transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={DOC_CONFIG.whitepaperUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group hidden items-center gap-1.5 rounded-full bg-white px-5 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-black transition-all hover:bg-indigo-400 hover:text-white sm:inline-flex"
              data-testid="nav-whitepaper"
            >
              Read Whitepaper
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <button
              onClick={() => setOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
              aria-label="Open menu"
              data-testid="nav-menu-open"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-[#030305]/95 backdrop-blur-2xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex h-16 items-center justify-between px-5">
              <span className="font-display text-sm font-bold uppercase tracking-[0.22em] text-white">
                Xenovia&nbsp;Prime
              </span>
              <button
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white"
                aria-label="Close menu"
                data-testid="nav-menu-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-6">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => handleNav(l.href)}
                  className="border-b border-white/5 py-4 text-left font-display text-2xl font-bold uppercase tracking-tight text-white"
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <div className="p-6">
              <a
                href={DOC_CONFIG.whitepaperUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-4 font-mono text-sm font-semibold uppercase tracking-[0.14em] text-black"
                data-testid="mobile-whitepaper"
              >
                Read Whitepaper
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
